import React, { useContext, useEffect, useState } from "react";
import { resetRecipientsCountOnRecipientStep } from "../../../../helpers/GiftCardHelper";
import { ChooseCardSubpageModel, IChooseCardSubpageModel } from "../../../../models/buy-giftcard/choose-card/ChooseCardSubpage.model";
import { ChooseCardStepModel, IChooseCardStepModel } from "../../../../models/buy-giftcard/choose-card/ChooseCardsStep.model";
import { ChooseRecipientsStepModel, IChooseRecipientsStepModel } from "../../../../models/buy-giftcard/choose-card/ChooseRecipientsStep.model";
import { ChooseSenderStepModel, IChooseSenderStepModel } from "../../../../models/buy-giftcard/choose-card/ChooseSenderStep.model";
import { BuyGiftCardStateEnum } from "../../../../models/enums/BuyGiftCardStateEnum";
import { BuyGiftCardSubpageEnum } from "../../../../models/enums/BuyGiftCardSubpageEnum";
import { IBuyGiftCardSubpageProps } from "../../../../models/interfaces/BuyGiftCardSubpage.interface";
import { AppSettingContext } from "../../../Application";
import ChoooseCardsStep from "../steps/ChoooseCardsStep";
import ChooseRecipientsStep from "../steps/ChooseRecipientsStep";
import ChooseSenderStep from "../steps/ChooseSenderStep";

interface IChooseCardSubpageProps extends IBuyGiftCardSubpageProps {
    subpageModel: IChooseCardSubpageModel;

    handleCardDetailsChange(model: IChooseCardStepModel): void;
    handleRecipientsChange(model: IChooseRecipientsStepModel): void;
    handleSubpageSubmit(model: IChooseCardSubpageModel): void;
}

const ChooseCardSubpage = (props: IChooseCardSubpageProps) => {
    const { cardSettings } = useContext(AppSettingContext);

    //#region STATE PROPERTIES

    const [subpageModel, setSubpageModel] = useState<IChooseCardSubpageModel>(props.subpageModel);
    const [isSaving, setIsSaving] = useState(false);
    const [stepsStatus, setStepsStatus] = useState({
        chooseCardDetails: ChooseCardStepModel.isValid(props.subpageModel.chooseCardDetails, cardSettings),
        chooseRecipients: ChooseRecipientsStepModel.isValid(props.subpageModel.chooseRecipients),
        chooseSender: false
    });

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        if (subpageModel) {
            setTimeout(() => {
                setIsSaving(false);
            }, 500);
        }
    }, [subpageModel]);

    useEffect(() => {
        if (stepsStatus.chooseCardDetails && stepsStatus.chooseRecipients && stepsStatus.chooseSender) {
            if (ChooseCardSubpageModel.isValid(subpageModel)) {
                props.handleSubpageSubmit(subpageModel);
            } else {
                console.error('Invalid ChooseCard subpage model');
                console.error(subpageModel);
            }
        }
    }, [stepsStatus]);

    //#endregion

    //#region CHOOSE CARDS STEP

    const handleChooseCardSubmit = (model: IChooseCardStepModel): void => {
        const isModelValid = ChooseCardStepModel.isValid(model, null);
        let isRecipientsStepValid = false;

        if (isModelValid) {
            setIsSaving(true);

            const responseModel = resetRecipientsCountOnRecipientStep(model.quantity, subpageModel.chooseRecipients);

            const newSubpageModel = {
                ...subpageModel,
                chooseCardDetails: model,
                chooseRecipients: {
                    ...subpageModel.chooseRecipients,
                    sendIndividually: responseModel.sendIndividually,
                    recipients: (responseModel as ChooseRecipientsStepModel).recipients,
                }
            };

            if (stepsStatus.chooseRecipients) {
                isRecipientsStepValid = ChooseRecipientsStepModel.isValid(newSubpageModel.chooseRecipients);
            }

            setSubpageModel(newSubpageModel);
            props.handleCardDetailsChange(model);
        }

        setStepsStatus({
            ...stepsStatus,
            chooseCardDetails: isModelValid,
            chooseRecipients: isRecipientsStepValid
        });
    };

    //#endregion

    //#region CHOOSE RECIPIENTS STEP

    const handleSendIndividuallyChange = (sendIndividually: boolean): void => {
        const recipients = [...subpageModel.chooseRecipients.recipients];

        if (sendIndividually) {
            while (recipients.length < subpageModel.chooseCardDetails.quantity) {
                recipients.push({
                    fullName: props.subpageModel?.chooseRecipients?.recipients[recipients.length]?.fullName ?? "",
                    email: props.subpageModel?.chooseRecipients?.recipients[recipients.length]?.email ?? "",
                    phone: props.subpageModel?.chooseRecipients?.recipients[recipients.length]?.phone ?? "",
                });
            }
        }

        setSubpageModel({
            ...subpageModel,
            chooseRecipients: {
                ...subpageModel.chooseRecipients,
                sendIndividually: sendIndividually,
                recipients: sendIndividually ? recipients : [subpageModel.chooseRecipients.recipients[0]]
            }
        });
    };

    const handleRecipientsSubmit = (model: IChooseRecipientsStepModel): void => {
        const isModelValid = ChooseRecipientsStepModel.isValid(model);

        if (isModelValid) {
            setIsSaving(true);
            setSubpageModel({
                ...subpageModel,
                chooseRecipients: model
            });

            props.handleRecipientsChange(model);
        }

        setStepsStatus({
            ...stepsStatus,
            chooseRecipients: isModelValid
        });
    };

    //#endregion

    //#region CHOOSE SENDER STEP

    const handleSenderSubmit = (model: IChooseSenderStepModel): void => {
        const isModelValid = ChooseSenderStepModel.isValid(model);
        if (isModelValid) {
            setIsSaving(true);
            setSubpageModel({
                ...subpageModel,
                chooseSender: model
            });
        }

        setStepsStatus({
            ...stepsStatus,
            chooseSender: isModelValid
        });
    };

    //#endregion

    //#region COMMON HELPERS

    const handleSetActiveStep = (newState: BuyGiftCardStateEnum): void => {
        props.handleStateChange(BuyGiftCardSubpageEnum.CHOOSE_CARD, newState);
    };

    //#endregion

    return (
        <>
            <ChoooseCardsStep
                stepModel={subpageModel.chooseCardDetails}
                isSaving={isSaving}
                activeStep={props.activeStep}

                handleSetActiveStep={handleSetActiveStep}
                handleSubmit={handleChooseCardSubmit}
            />

            <ChooseRecipientsStep
                stepModel={subpageModel.chooseRecipients}
                isSaving={isSaving}
                activeStep={props.activeStep}
                isAvailable={stepsStatus.chooseCardDetails}
                cardsQuantity={subpageModel.chooseCardDetails?.quantity ?? 0}

                handleSetActiveStep={handleSetActiveStep}
                handleSendIndividuallyChange={handleSendIndividuallyChange}
                handleSubmit={handleRecipientsSubmit}
            />

            <ChooseSenderStep
                stepModel={subpageModel.chooseSender}
                isSaving={isSaving}
                activeStep={props.activeStep}
                isAvailable={stepsStatus.chooseCardDetails && stepsStatus.chooseRecipients}

                handleSetActiveStep={handleSetActiveStep}
                handleSubmit={handleSenderSubmit}
            />
        </>
    );
};

export default ChooseCardSubpage;